/**
 * The office, as plain geometry: team pods of desks, meeting rooms, a kitchen and a lounge behind
 * walls with doors, and the rules of moving through them. No React and no DOM, so the shape of the
 * building and the way people path through it are testable, and the page only draws.
 */
export interface Rect {
  x: number
  y: number
  w: number
  h: number
}
export interface Pos {
  x: number
  y: number
}
export interface Desk extends Rect {
  userId: string
  team: string
  /** Where the person sits: just below the desk. */
  seat: Pos
}
export type PropKind = 'plant' | 'cooler' | 'sofa' | 'table' | 'counter' | 'fridge' | 'whiteboard' | 'arcade' | 'bookshelf' | 'printer' | 'pingpong' | 'beanbag'
export interface Prop extends Rect {
  kind: PropKind
  /** Decoration that sits against a wall does not stop anyone. */
  blocks: boolean
}
export type ZoneKind = 'pod' | 'meeting' | 'kitchen' | 'lounge' | 'hall'
export interface Zone extends Rect {
  id: string
  name: string
  kind: ZoneKind
}
export interface World {
  w: number
  h: number
  desks: Desk[]
  props: Prop[]
  walls: Rect[]
  zones: Zone[]
  /** Everything a person cannot walk through. */
  blockers: Rect[]
  spawn: Pos
}
export interface OfficePerson {
  userId: string
  displayName: string
  team: string | null
}

export const DESK_W = 110
export const DESK_H = 50
export const PLAYER = 28
export const OTHERS = 'Everyone else'

const WALL = 10
const WS_W = 1100
const POD_COLS = 3
const CELL_W = 160
const CELL_H = 130
const POD_HEAD = 40
const POD_W = POD_COLS * CELL_W
const DOOR = 96

function wallsAround(r: Rect, doorCenterX: number): Rect[] {
  const left = doorCenterX - DOOR / 2
  const right = doorCenterX + DOOR / 2
  return [
    { x: r.x, y: r.y, w: r.w, h: WALL },
    { x: r.x, y: r.y, w: WALL, h: r.h },
    { x: r.x + r.w - WALL, y: r.y, w: WALL, h: r.h },
    { x: r.x, y: r.y + r.h - WALL, w: left - r.x, h: WALL },
    { x: right, y: r.y + r.h - WALL, w: r.x + r.w - right, h: WALL },
  ]
}

const world_pods = (zones: readonly Zone[]) => zones.filter((z) => z.kind === 'pod')

export function layoutOffice(people: readonly OfficePerson[]): World {
  const groups = new Map<string, OfficePerson[]>()
  for (const p of people) {
    const key = p.team?.trim() || OTHERS
    groups.set(key, [...(groups.get(key) ?? []), p])
  }
  const names = [...groups.keys()].sort((a, b) => (a === OTHERS ? 1 : b === OTHERS ? -1 : a.localeCompare(b)))

  // Pods flow two to a row; a row is as tall as its tallest pod.
  const podRows = (name: string) => Math.ceil((groups.get(name)?.length ?? 0) / POD_COLS)
  const podH = (name: string) => POD_HEAD + podRows(name) * CELL_H + 20
  const rowCount = Math.ceil(names.length / 2)
  const rowY: number[] = []
  let cursor = 50
  for (let r = 0; r < rowCount; r++) {
    rowY.push(cursor)
    cursor += Math.max(...names.slice(r * 2, r * 2 + 2).map(podH)) + 20
  }
  const wsBottom = cursor

  const desks: Desk[] = []
  const zones: Zone[] = []
  names.forEach((name, i) => {
    const px = 30 + (i % 2) * (POD_W + 50)
    const py = rowY[Math.floor(i / 2)]!
    zones.push({ id: `pod:${name}`, name, kind: 'pod', x: px - 10, y: py, w: POD_W + 20, h: podH(name) })
    const members = [...(groups.get(name) ?? [])].sort((a, b) => a.displayName.localeCompare(b.displayName) || a.userId.localeCompare(b.userId))
    members.forEach((m, j) => {
      const x = px + (j % POD_COLS) * CELL_W + (CELL_W - DESK_W) / 2
      const y = py + POD_HEAD + Math.floor(j / POD_COLS) * CELL_H + 10
      desks.push({ userId: m.userId, team: name, x, y, w: DESK_W, h: DESK_H, seat: { x: x + DESK_W / 2, y: y + DESK_H + 24 } })
    })
  })

  const h = Math.max(wsBottom + 100, 860)
  const w = 1670

  const meetA: Rect = { x: 1130, y: 40, w: 250, h: 200 }
  const meetB: Rect = { x: 1400, y: 40, w: 250, h: 200 }
  zones.push(
    { id: 'room:standup', name: 'Standup room', kind: 'meeting', ...meetA },
    { id: 'room:huddle', name: 'Huddle room', kind: 'meeting', ...meetB },
    { id: 'kitchen', name: 'Kitchen', kind: 'kitchen', x: 1130, y: 290, w: 520, h: 200 },
    { id: 'lounge', name: 'Lounge', kind: 'lounge', x: 1130, y: 540, w: 520, h: h - 560 },
    { id: 'entrance', name: 'Entrance', kind: 'hall', x: 30, y: h - 90, w: WS_W - 60, h: 70 },
  )

  // The divider between the workspace and the rest of the floor has a door at the kitchen and one at the lounge.
  const gaps: Array<[number, number]> = [[345, 455], [585, 695]]
  const walls: Rect[] = []
  let from = 0
  for (const [a, b] of gaps) {
    walls.push({ x: WS_W, y: from, w: WALL, h: a - from })
    from = b
  }
  walls.push({ x: WS_W, y: from, w: WALL, h: h - from })
  walls.push(...wallsAround(meetA, meetA.x + meetA.w / 2), ...wallsAround(meetB, meetB.x + meetB.w / 2))

  const props: Prop[] = [
    { kind: 'plant', x: 1054, y: 50, w: 36, h: 36, blocks: true },
    { kind: 'plant', x: 1054, y: h - 130, w: 36, h: 36, blocks: true },
    { kind: 'plant', x: 40, y: h - 84, w: 36, h: 36, blocks: true },
    { kind: 'plant', x: 1010, y: h - 84, w: 36, h: 36, blocks: true },
    { kind: 'printer', x: 1056, y: 300, w: 34, h: 34, blocks: true },
    { kind: 'bookshelf', x: 60, y: 8, w: 200, h: 26, blocks: true },
    { kind: 'bookshelf', x: 640, y: 8, w: 200, h: 26, blocks: true },
    // A plant in the corner of every pod, out of the way of the desks.
    ...world_pods(zones).map((z): Prop => ({ kind: 'plant', x: z.x + z.w - 40, y: z.y + 6, w: 28, h: 28, blocks: true })),
    // Between the meeting rooms and the kitchen
    { kind: 'plant', x: 1136, y: 252, w: 28, h: 28, blocks: true },
    { kind: 'plant', x: 1630, y: 252, w: 28, h: 28, blocks: true },
    { kind: 'plant', x: 1616, y: 440, w: 30, h: 30, blocks: true },
    // Meeting rooms
    { kind: 'table', x: meetA.x + 65, y: meetA.y + 70, w: 120, h: 56, blocks: true },
    { kind: 'table', x: meetB.x + 65, y: meetB.y + 70, w: 120, h: 56, blocks: true },
    { kind: 'whiteboard', x: meetA.x + 30, y: meetA.y + WALL, w: 190, h: 14, blocks: false },
    { kind: 'whiteboard', x: meetB.x + 30, y: meetB.y + WALL, w: 190, h: 14, blocks: false },
    // Kitchen
    { kind: 'counter', x: 1150, y: 300, w: 300, h: 36, blocks: true },
    { kind: 'fridge', x: 1470, y: 300, w: 56, h: 56, blocks: true },
    { kind: 'cooler', x: 1560, y: 300, w: 36, h: 44, blocks: true },
    { kind: 'table', x: 1250, y: 400, w: 140, h: 60, blocks: true },
    // Lounge
    { kind: 'sofa', x: 1150, y: 560, w: 200, h: 56, blocks: true },
    { kind: 'sofa', x: 1420, y: 560, w: 200, h: 56, blocks: true },
    { kind: 'table', x: 1330, y: 660, w: 120, h: 48, blocks: true },
    { kind: 'arcade', x: 1580, y: 660, w: 56, h: 64, blocks: true },
    { kind: 'pingpong', x: 1170, y: 730, w: 180, h: 96, blocks: true },
    { kind: 'beanbag', x: 1420, y: 760, w: 56, h: 56, blocks: true },
    { kind: 'beanbag', x: 1500, y: 770, w: 56, h: 56, blocks: true },
    { kind: 'plant', x: 1150, y: h - 90, w: 40, h: 40, blocks: true },
    { kind: 'plant', x: 1610, y: h - 90, w: 40, h: 40, blocks: true },
  ]

  const blockers = [...desks, ...walls, ...props.filter((p) => p.blocks)]
  return { w, h, desks, props, walls, zones, blockers, spawn: { x: WS_W / 2, y: h - 50 } }
}

const overlaps = (a: Rect, b: Rect) => a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y
const box = (p: Pos): Rect => ({ x: p.x - PLAYER / 2, y: p.y - PLAYER / 2, w: PLAYER, h: PLAYER })
const inside = (r: Rect, p: Pos) => p.x >= r.x && p.x <= r.x + r.w && p.y >= r.y && p.y <= r.y + r.h

const clampToWorld = (p: Pos, world: World): Pos => ({
  x: Math.min(world.w - PLAYER / 2, Math.max(PLAYER / 2, p.x)),
  y: Math.min(world.h - PLAYER / 2, Math.max(PLAYER / 2, p.y)),
})

/** True if someone standing at `p` would be inside a wall, desk or piece of furniture, or off the floor. */
export function blockedAt(world: World, p: Pos): boolean {
  if (p.x < PLAYER / 2 || p.y < PLAYER / 2 || p.x > world.w - PLAYER / 2 || p.y > world.h - PLAYER / 2) return true
  const b = box(p)
  return world.blockers.some((r) => overlaps(b, r))
}

/**
 * Moves by `dx, dy` (already scaled to this frame) one axis at a time, so walking into a desk or
 * along a wall slides instead of sticking.
 */
export function step(pos: Pos, dx: number, dy: number, world: World): Pos {
  let next = pos
  const tryX = clampToWorld({ x: pos.x + dx, y: pos.y }, world)
  if (!blockedAt(world, tryX)) next = tryX
  const tryY = clampToWorld({ x: next.x, y: pos.y + dy }, world)
  if (!blockedAt(world, tryY)) next = tryY
  return next
}

/** Where to stand when a floor change has left you inside something: the closest free spot. */
export function unstick(pos: Pos, world: World): Pos {
  if (!blockedAt(world, pos)) return pos
  for (let r = 8; r <= 400; r += 8) {
    for (let a = 0; a < 16; a++) {
      const p = { x: pos.x + Math.cos((a / 16) * Math.PI * 2) * r, y: pos.y + Math.sin((a / 16) * Math.PI * 2) * r }
      if (!blockedAt(world, p)) return p
    }
  }
  return world.spawn
}

/** The desk you are close enough to talk to, if any. */
export function nearestDesk(pos: Pos, world: World, reach = 84): Desk | null {
  let best: Desk | null = null
  let bestD = reach
  for (const desk of world.desks) {
    // Distance to the desk's rectangle, so standing at its side counts as well as in front.
    const cx = Math.max(desk.x, Math.min(pos.x, desk.x + desk.w))
    const cy = Math.max(desk.y, Math.min(pos.y, desk.y + desk.h))
    const d = Math.hypot(pos.x - cx, pos.y - cy)
    if (d <= bestD) {
      best = desk
      bestD = d
    }
  }
  return best
}

/** The closest of `others` within `radius`, if anyone. */
export function nearestPerson<T extends { userId: string; x: number; y: number }>(pos: Pos, others: readonly T[], radius = 90): T | null {
  let best: T | null = null
  let bestD = radius
  for (const o of others) {
    const d = Math.hypot(o.x - pos.x, o.y - pos.y)
    if (d <= bestD) {
      best = o
      bestD = d
    }
  }
  return best
}

/** The named place a point is in, or the hallway between them. */
export function zoneAt(world: World, p: Pos): { id: string; name: string; kind: ZoneKind } {
  const z = world.zones.find((zone) => inside(zone, p))
  return z ? { id: z.id, name: z.name, kind: z.kind } : { id: 'hallway', name: 'Hallway', kind: 'hall' }
}

/** A unit direction from `from` toward `to`, or null once you are there. */
export function headingTo(from: Pos, to: Pos, arrive = 6): Pos | null {
  const dx = to.x - from.x
  const dy = to.y - from.y
  const d = Math.hypot(dx, dy)
  return d <= arrive ? null : { x: dx / d, y: dy / d }
}

/* ------------------------------------------------------------------ finding a way */

const NAV = 20
export interface Nav {
  world: World
  cols: number
  rows: number
  blocked: Uint8Array
}

/** A coarse grid of where a person fits, built once per floor. */
export function buildNav(world: World): Nav {
  const cols = Math.ceil(world.w / NAV)
  const rows = Math.ceil(world.h / NAV)
  const blocked = new Uint8Array(cols * rows)
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (blockedAt(world, { x: c * NAV + NAV / 2, y: r * NAV + NAV / 2 })) blocked[r * cols + c] = 1
    }
  }
  return { world, cols, rows, blocked }
}

const cellOf = (nav: Nav, p: Pos) => ({
  c: Math.min(nav.cols - 1, Math.max(0, Math.floor(p.x / NAV))),
  r: Math.min(nav.rows - 1, Math.max(0, Math.floor(p.y / NAV))),
})
const centerOf = (c: number, r: number): Pos => ({ x: c * NAV + NAV / 2, y: r * NAV + NAV / 2 })

function nearestFree(nav: Nav, start: { c: number; r: number }): { c: number; r: number } | null {
  if (!nav.blocked[start.r * nav.cols + start.c]) return start
  for (let radius = 1; radius <= 14; radius++) {
    let best: { c: number; r: number } | null = null
    let bestD = Infinity
    for (let dr = -radius; dr <= radius; dr++) {
      for (let dc = -radius; dc <= radius; dc++) {
        if (Math.max(Math.abs(dr), Math.abs(dc)) !== radius) continue
        const c = start.c + dc
        const r = start.r + dr
        if (c < 0 || r < 0 || c >= nav.cols || r >= nav.rows || nav.blocked[r * nav.cols + c]) continue
        const d = dr * dr + dc * dc
        if (d < bestD) {
          bestD = d
          best = { c, r }
        }
      }
    }
    if (best) return best
  }
  return null
}

/** Can you walk in a straight line from `a` to `b` without touching anything? */
export function canWalk(world: World, a: Pos, b: Pos): boolean {
  const d = Math.hypot(b.x - a.x, b.y - a.y)
  const n = Math.max(1, Math.ceil(d / 6))
  for (let i = 1; i <= n; i++) {
    if (blockedAt(world, { x: a.x + ((b.x - a.x) * i) / n, y: a.y + ((b.y - a.y) * i) / n })) return false
  }
  return true
}

/**
 * Waypoints from `from` to `to` around desks, furniture and through doors, or null if there is no
 * way. A click on a desk or a wall walks you to the closest free spot beside it.
 */
export function findPath(nav: Nav, from: Pos, to: Pos): Pos[] | null {
  const world = nav.world
  const start = nearestFree(nav, cellOf(nav, from))
  const goal = nearestFree(nav, cellOf(nav, to))
  if (!start || !goal) return null
  const idx = (c: number, r: number) => r * nav.cols + c
  const g = new Map<number, number>([[idx(start.c, start.r), 0]])
  const parent = new Map<number, number>()
  const open = new Set<number>([idx(start.c, start.r)])
  const closed = new Set<number>()
  const heuristic = (c: number, r: number) => {
    const dx = Math.abs(c - goal.c)
    const dy = Math.abs(r - goal.r)
    return dx + dy + (Math.SQRT2 - 2) * Math.min(dx, dy)
  }
  const goalIdx = idx(goal.c, goal.r)
  let found = false
  while (open.size) {
    let cur = -1
    let curF = Infinity
    for (const n of open) {
      const c = n % nav.cols
      const r = Math.floor(n / nav.cols)
      const f = (g.get(n) ?? Infinity) + heuristic(c, r)
      if (f < curF) {
        curF = f
        cur = n
      }
    }
    if (cur === goalIdx) {
      found = true
      break
    }
    open.delete(cur)
    closed.add(cur)
    const cc = cur % nav.cols
    const cr = Math.floor(cur / nav.cols)
    for (let dr = -1; dr <= 1; dr++) {
      for (let dc = -1; dc <= 1; dc++) {
        if (!dr && !dc) continue
        const c = cc + dc
        const r = cr + dr
        if (c < 0 || r < 0 || c >= nav.cols || r >= nav.rows || nav.blocked[idx(c, r)]) continue
        // No cutting a corner between two blocked cells.
        if (dr && dc && (nav.blocked[idx(cc + dc, cr)] || nav.blocked[idx(cc, cr + dr)])) continue
        const ni = idx(c, r)
        if (closed.has(ni)) continue
        const cost = (g.get(cur) ?? 0) + (dr && dc ? Math.SQRT2 : 1)
        if (cost < (g.get(ni) ?? Infinity)) {
          g.set(ni, cost)
          parent.set(ni, cur)
          open.add(ni)
        }
      }
    }
  }
  if (!found) return null

  const cells: Pos[] = []
  for (let n: number | undefined = goalIdx; n !== undefined; n = parent.get(n)) cells.push(centerOf(n % nav.cols, Math.floor(n / nav.cols)))
  cells.reverse()
  // End exactly where you clicked when that spot is open and in sight of the last cell.
  const last = cells[cells.length - 1]!
  const end = !blockedAt(world, to) && canWalk(world, last, to) ? to : last
  const line = [from, ...cells.slice(1), ...(end === last ? [] : [end])]

  // Pull the string tight: keep only the corners.
  const out: Pos[] = []
  let a = 0
  while (a < line.length - 1) {
    let j = line.length - 1
    while (j > a + 1 && !canWalk(world, line[a]!, line[j]!)) j--
    out.push(line[j]!)
    a = j
  }
  return out
}
